import { db } from './firebase';
import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  increment,
  writeBatch
} from 'firebase/firestore';

const IDEAS_COLLECTION = 'ideas';

/**
 * Create a new idea
 * @param {Object} ideaData - The idea data
 * @returns {Promise<string>} - The ID of the created idea
 */
export const createIdea = async (ideaData) => {
  try {
    const ideasRef = collection(db, IDEAS_COLLECTION);
    const docRef = await addDoc(ideasRef, {
      ...ideaData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating idea:', error);
    throw new Error('Failed to create idea');
  }
};

/**
 * Get a single idea by ID
 * @param {string} ideaId - The idea ID
 * @returns {Promise<Object>} - The idea data
 */
export const getIdea = async (ideaId) => {
  try {
    const ideaRef = doc(db, IDEAS_COLLECTION, ideaId);
    const ideaSnap = await getDoc(ideaRef);
    
    if (ideaSnap.exists()) {
      return {
        id: ideaSnap.id,
        ...ideaSnap.data(),
      };
    } else {
      throw new Error('Idea not found');
    }
  } catch (error) {
    console.error('Error getting idea:', error);
    throw error;
  }
};

/**
 * Get all ideas with optional filters
 * @param {Object} filters - Filter options
 * @returns {Promise<Array>} - Array of ideas
 */
export const getIdeas = async (filters = {}) => {
  try {
    console.log('getIdeas called with filters:', filters);
    const ideasRef = collection(db, IDEAS_COLLECTION);
    let q = query(ideasRef);

    // Apply filters
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    if (filters.platform) {
      q = query(q, where('platform', '==', filters.platform));
    }

    if (filters.authorId) {
      console.log('Filtering by authorId:', filters.authorId);
      q = query(q, where('authorId', '==', filters.authorId));
    }

    if (filters.tags && filters.tags.length > 0) {
      q = query(q, where('tags', 'array-contains-any', filters.tags));
    }

    // Apply sorting (skip for filtered queries to avoid index requirements)
    // When filtering by authorId, we'll sort client-side
    if (!filters.authorId) {
      if (filters.sortBy === 'votes') {
        q = query(q, orderBy('votes', 'desc'));
      } else if (filters.sortBy === 'roi') {
        q = query(q, orderBy('roi', 'desc'));
      } else {
        q = query(q, orderBy('createdAt', 'desc'));
      }
    }

    // Apply limit
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }

    console.log('Executing Firestore query...');
    const querySnapshot = await getDocs(q);
    console.log('Query executed. Docs found:', querySnapshot.size);
    
    let ideas = [];
    querySnapshot.forEach((doc) => {
      ideas.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Sort client-side if we skipped server-side sorting
    if (filters.authorId) {
      ideas.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime; // Descending order (newest first)
      });
    }

    console.log('Returning ideas:', ideas.length);
    return ideas;
  } catch (error) {
    console.error('Error getting ideas - Full error:', error);
    console.error('Error code:', error.code);
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied. Please check Firestore security rules.');
    } else if (error.message?.includes('Missing or insufficient permissions')) {
      throw new Error('Missing permissions. Update Firestore rules (see FIRESTORE_SETUP.md)');
    } else {
      throw new Error(`Failed to fetch ideas: ${error.message}`);
    }
  }
};

/**
 * Get ideas by a specific user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} - Array of user's ideas
 */
export const getUserIdeas = async (userId) => {
  try {
    console.log('Fetching ideas for user:', userId);
    const ideas = await getIdeas({ authorId: userId });
    console.log('Successfully fetched ideas:', ideas);
    return ideas;
  } catch (error) {
    console.error('getUserIdeas error:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    throw error;
  }
};

/**
 * Check if a user has any approved ideas
 * @param {string} userId - The user ID
 * @returns {Promise<boolean>} - True if user has at least one approved idea (active or resolved)
 */
export const hasUserApprovedIdea = async (userId) => {
  try {
    // Fetch all user ideas and filter client-side to avoid composite index requirement
    const ideasRef = collection(db, IDEAS_COLLECTION);
    const q = query(
      ideasRef,
      where('authorId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    
    // Check if any idea has moderationStatus 'approved'
    // This includes both active and resolved approved ideas
    // Once a user has one approved idea, they can post freely
    let hasApproved = false;
    querySnapshot.forEach((doc) => {
      const idea = doc.data();
      if (idea.moderationStatus === 'approved') {
        hasApproved = true;
      }
    });
    
    return hasApproved;
  } catch (error) {
    console.error('Error checking for approved ideas:', error);
    // If there's an error, default to requiring approval (safer)
    return false;
  }
};

/**
 * Update an idea
 * @param {string} ideaId - The idea ID
 * @param {Object} updates - The fields to update
 * @returns {Promise<void>}
 */
export const updateIdea = async (ideaId, updates) => {
  try {
    const ideaRef = doc(db, IDEAS_COLLECTION, ideaId);
    await updateDoc(ideaRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating idea:', error);
    throw new Error('Failed to update idea');
  }
};

/**
 * Delete an idea
 * @param {string} ideaId - The idea ID
 * @returns {Promise<void>}
 */
export const deleteIdea = async (ideaId) => {
  try {
    const ideaRef = doc(db, IDEAS_COLLECTION, ideaId);
    await deleteDoc(ideaRef);
  } catch (error) {
    console.error('Error deleting idea:', error);
    throw new Error('Failed to delete idea');
  }
};

/**
 * Increment view count for an idea
 * @param {string} ideaId - The idea ID
 * @returns {Promise<void>}
 */
export const incrementViews = async (ideaId) => {
  try {
    const ideaRef = doc(db, IDEAS_COLLECTION, ideaId);
    await updateDoc(ideaRef, {
      views: increment(1),
    });
  } catch (error) {
    console.error('Error incrementing views:', error);
    // Don't throw error for view tracking
  }
};

/**
 * Vote on an idea (upvote/downvote)
 * @param {string} ideaId - The idea ID
 * @param {number} value - Vote value (1 for upvote, -1 for downvote)
 * @returns {Promise<void>}
 */
export const voteIdea = async (ideaId, value) => {
  try {
    const ideaRef = doc(db, IDEAS_COLLECTION, ideaId);
    await updateDoc(ideaRef, {
      votes: increment(value),
    });
  } catch (error) {
    console.error('Error voting on idea:', error);
    throw new Error('Failed to vote on idea');
  }
};

/**
 * Resolve an idea with outcome and ROI
 * @param {string} ideaId - The idea ID
 * @param {Object} resolution - Resolution data (outcome, actualPrice, roi)
 * @returns {Promise<void>}
 */
export const resolveIdea = async (ideaId, resolution) => {
  try {
    const ideaRef = doc(db, IDEAS_COLLECTION, ideaId);
    await updateDoc(ideaRef, {
      status: 'resolved',
      outcome: resolution.outcome,
      actualPrice: resolution.actualPrice,
      roi: resolution.roi,
      resolvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error resolving idea:', error);
    throw new Error('Failed to resolve idea');
  }
};

/**
 * Get trending ideas (most voted in last 7 days)
 * @param {number} limitCount - Number of ideas to return
 * @returns {Promise<Array>} - Array of trending ideas
 */
export const getTrendingIdeas = async (limitCount = 10) => {
  try {
    const ideasRef = collection(db, IDEAS_COLLECTION);
    const q = query(
      ideasRef,
      where('status', '==', 'active'),
      orderBy('votes', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const ideas = [];
    querySnapshot.forEach((doc) => {
      ideas.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return ideas;
  } catch (error) {
    console.error('Error getting trending ideas:', error);
    throw new Error('Failed to fetch trending ideas');
  }
};

/**
 * Search ideas by title or description
 * @param {string} searchTerm - The search term
 * @returns {Promise<Array>} - Array of matching ideas
 */
export const searchIdeas = async (searchTerm) => {
  try {
    // Note: For production, consider using Algolia or another search service
    // This is a basic implementation
    const ideasRef = collection(db, IDEAS_COLLECTION);
    const q = query(ideasRef, orderBy('createdAt', 'desc'));
    
    const querySnapshot = await getDocs(q);
    const ideas = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const lowerSearchTerm = searchTerm.toLowerCase();
      
      if (
        data.title?.toLowerCase().includes(lowerSearchTerm) ||
        data.description?.toLowerCase().includes(lowerSearchTerm) ||
        data.tags?.some(tag => tag.toLowerCase().includes(lowerSearchTerm))
      ) {
        ideas.push({
          id: doc.id,
          ...data,
        });
      }
    });

    return ideas;
  } catch (error) {
    console.error('Error searching ideas:', error);
    throw new Error('Failed to search ideas');
  }
};

/**
 * Check and mark expired ideas as settled
 * @returns {Promise<number>} - Number of ideas marked as settled
 */
export const checkAndMarkExpiredIdeas = async () => {
  try {
    const ideasRef = collection(db, IDEAS_COLLECTION);
    const q = query(
      ideasRef,
      where('status', '==', 'active')
    );
    
    const querySnapshot = await getDocs(q);
    const now = new Date();
    const batch = writeBatch(db);
    let count = 0;
    
    querySnapshot.forEach((docSnapshot) => {
      const idea = docSnapshot.data();
      
      if (idea.marketExpirationDate) {
        const expirationDate = new Date(idea.marketExpirationDate);
        
        // If market has expired, mark as settled
        if (expirationDate < now) {
          batch.update(docSnapshot.ref, {
            status: 'settled',
            settledAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          count++;
        }
      }
    });
    
    if (count > 0) {
      await batch.commit();
      console.log(`Marked ${count} ideas as settled`);
    }
    
    return count;
  } catch (error) {
    console.error('Error checking expired ideas:', error);
    throw new Error('Failed to check expired ideas');
  }
};

